import type { ComposerHistoryTaskStatus } from "@bash/shared";
import type { ComposerHistoryEntry } from "@/stores/composerHistoryStore";
import { cn } from "@/lib/utils";
import { CheckIcon } from "@/components/icons/CheckIcon";
import { ChevronRightIcon } from "@/components/icons/ChevronRightIcon";
import { Glyph } from "./glyphs";

/** The words a task's state reads as. Out of credits is its own word, never
 *  "Stopped" (founder ruling 09-03). */
export const STATUS_LABEL: Record<ComposerHistoryTaskStatus, string> = {
  running: "Running",
  needs_you: "Needs you",
  scheduled: "Scheduled",
  done: "Done",
  failed: "Failed",
  stopped: "Stopped",
  out_of_credits: "Out of credits",
};

const STATUS_INK: Partial<Record<ComposerHistoryTaskStatus, string>> = {
  running: "text-hb-name",
  needs_you: "text-gold-ink",
  done: "text-kind-green",
  failed: "text-kind-red",
};

const MARK = "grid size-4.5 flex-none place-items-center rounded-full";

/** The Recents row's running mark (RecentsPanel.tsx), the same classes: a
 *  copper dot and its pulse ring. */
const RUNNING_MARK =
  "relative before:size-2.25 before:rounded-full before:bg-hb-name before:content-[''] after:absolute after:inset-0 after:animate-bh-pulse after:rounded-full after:border-2 after:border-hb-name after:content-['']";

function StatusMark({ status }: { status?: ComposerHistoryTaskStatus }) {
  switch (status) {
    case "running":
      return (
        <span
          className={cn(MARK, RUNNING_MARK)}
          data-status={status}
          aria-hidden="true"
        />
      );
    case "done":
      return (
        <span
          className={cn(MARK, "bg-kind-green-14 text-kind-green")}
          data-status={status}
          aria-hidden="true"
        >
          <CheckIcon width={10} height={10} strokeWidth="3.4" />
        </span>
      );
    case "failed":
      return (
        <span
          className={cn(MARK, "bg-kind-red-14 text-kind-red")}
          data-status={status}
          aria-hidden="true"
        >
          <Glyph name="cross" strokeWidth={2.2} className="size-2.5" />
        </span>
      );
    case "needs_you":
      return (
        <span
          className={cn(
            MARK,
            "bg-kind-amber/14 before:size-2 before:rounded-full before:bg-kind-amber before:content-['']"
          )}
          data-status={status}
          aria-hidden="true"
        />
      );
    default:
      return (
        <span
          className={cn(MARK, "bg-bg-muted")}
          data-status={status ?? "unknown"}
          aria-hidden="true"
        />
      );
  }
}

/** The round chevron at the card's end, the task line's own (PillThread's
 *  .bh-bright__go), lit when the card is hovered. */
function Go({ slim }: { slim?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "transition-bh-hover grid flex-none place-items-center rounded-full border border-border-subtle bg-bg-muted text-fg-muted group-hover/card:bg-border-default group-hover/card:text-fg-default",
        slim ? "size-6" : "size-7"
      )}
    >
      <ChevronRightIcon className={slim ? "size-3" : "size-3.5"} />
    </span>
  );
}

const CARD =
  "group/card transition-bh-act grid items-center rounded-12 border text-left text-inherit [font:inherit] enabled:cursor-pointer enabled:hover:border-border-muted enabled:hover:bg-bg-subtle";

/**
 * What an ask became (spec 2026-09-24 §1): the full card for a task it
 * created (state mark, title, the state's word and one line), the slim
 * "Added to <title>" for a follow-up. The card opens the task. With no task
 * joined (unknown or gone) it shows the stored title and no state word.
 */
export function TaskCard({
  entry,
  slim = false,
  onOpen,
}: {
  entry: ComposerHistoryEntry;
  slim?: boolean;
  /** Absent when the row carries no task id: the card then opens nothing. */
  onOpen?: () => void;
}) {
  const title = entry.task?.title || entry.text;
  const door = {
    type: "button" as const,
    disabled: !onOpen,
    onClick: onOpen,
    "aria-label": `Open task: ${title}`,
    "data-testid": "history-task-card",
  };

  if (slim) {
    return (
      <button
        {...door}
        data-slim=""
        className={cn(
          CARD,
          "grid-cols-[18px_minmax(0,1fr)_auto] gap-2 border-border-subtle bg-transparent py-1 pr-1.5 pl-2"
        )}
      >
        <span
          className={cn(MARK, "bg-bg-muted text-fg-subtle")}
          aria-hidden="true"
        >
          <Glyph name="plus" strokeWidth={2} className="size-3" />
        </span>
        <span className="truncate text-3.5 text-fg-muted">
          Added to <b className="font-medium text-fg-default">{title}</b>
        </span>
        {onOpen && <Go slim />}
      </button>
    );
  }

  const status = entry.task?.status;
  const line = entry.task?.line;
  return (
    <button
      {...door}
      className={cn(
        CARD,
        "w-full max-w-115 grid-cols-[18px_minmax(0,1fr)_auto] gap-3 border-border-default bg-bg-elevated py-2.5 pr-2.5 pl-3"
      )}
    >
      <StatusMark status={status} />
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate text-3.5 font-medium text-fg-default">
          {title}
        </span>
        {status && (
          <span className="truncate text-3 text-fg-muted">
            <span
              className={cn("font-medium", STATUS_INK[status])}
              data-testid="history-task-status"
            >
              {STATUS_LABEL[status]}
            </span>
            {line && <> · {line}</>}
          </span>
        )}
      </span>
      {onOpen && <Go />}
    </button>
  );
}
